import type { RaAgentData } from './types';

/** Appointment Reminders agent page mock (board 5 / spec 03 rev 6). Last 30 days. */
export const REMINDERS_AGENT: RaAgentData = {
  practiceName: 'Alamo Heights Dental',
  // Counted from the steps, and it carries the hold state (§6). "working since
  // 8:00am" was a timestamp nobody could verify and nothing produced.
  eyebrow: '',
  titleLead: 'Appointment',
  titleAccent: 'Reminders',
  // A location id is an internal key. The practice knows where it is.
  subtitle: 'Email · Text · AI call · Alamo Heights Dental (San Antonio, TX)',
  waiting: { count: 2, names: 'Carlos Reyes · Dana Whitfield' },
  hero: {
    confirmed: 812,
    sent: 1204,
    caption: 'confirmed before the visit',
    // Named as the steps are named in Settings, so the two pages describe the
    // same sequence (§6). 47 + 690 + 52 + 23 = 812.
    segments: [
      { label: '14 days before · email', value: 47 },
      { label: '3 days before · text', value: 690 },
      { label: '2 days before · AI call', value: 52 },
      { label: '1 day before · text', value: 23 },
    ],
    // 812 of 1,212 appointments → 67 per 100 APPOINTMENTS, not per 100 sends.
    appointments: 1212,
  },
  sideCards: [
    // 1,204 was one step's sends, not the total. The channel rows add to 2,584.
    { label: 'Sent', value: '2,584', delta: '▲11.0%', caption: 'texts, emails and calls' },
    { label: 'Patients reached', value: '1,142', delta: '▲12.4%', caption: 'unique patients' },
  ],
  // Confirm rate is GONE — the hero's per-100 line already says it, and two
  // separately-computed confirm rates on one page is how this page started
  // disagreeing with itself (§6).
  kpis: [
    {
      key: 'noshow',
      label: 'No-show rate',
      value: '4.1%',
      caption: 'of booked visits · 1.9% confirmed, 11.4% not',
    },
    {
      key: 'complications',
      label: 'Complications caught',
      value: '7',
      // The post-op channel row was removed; its figures belong here, on the
      // thing they actually measure.
      caption: 'from 124 checks · 81% reached',
    },
  ],
  weeks: [
    { label: 'Jun 30', confirmed: 168 },
    { label: 'Jul 7', confirmed: 182 },
    { label: 'Jul 14', confirmed: 176 },
    { label: 'Jul 21', confirmed: 195 },
    { label: 'Jul 28', confirmed: 188 },
    { label: 'Aug 4', confirmed: 201 },
    { label: 'Aug 11', confirmed: 210 },
    { label: 'Aug 18', confirmed: 199 },
    { label: 'Aug 25', confirmed: 214 },
    { label: 'Sep 1', confirmed: 221 },
    { label: 'Sep 8', confirmed: 208 },
    { label: 'Sep 15', confirmed: 226 },
    { label: 'Sep 22', confirmed: 231 },
  ],
  steps: [
    {
      key: 's-email',
      before: true,
      dot: 'var(--ai-3)',
      name: '14 days before · email',
      purpose: 'Confirm · email',
      offset: '14 d',
      sent: '430',
      kpiValue: '11%',
      kpiLabel: 'confirm rate',
    },
    {
      key: 's-text3',
      before: true,
      dot: 'var(--success)',
      name: '3 days before · text',
      purpose: 'Confirm · text',
      offset: '3 d',
      sent: '1,204',
      kpiValue: '57%',
      kpiLabel: 'confirm rate',
    },
    {
      key: 's-voice',
      before: true,
      dot: 'var(--gold)',
      name: '2 days before · AI call',
      purpose: 'Confirm · voice · not confirmed only',
      offset: '2 d',
      sent: '186',
      kpiValue: '28%',
      kpiLabel: 'confirm rate',
    },
    {
      key: 's-text1',
      before: true,
      dot: 'var(--ai-3)',
      name: '1 day before · text',
      purpose: 'What to bring · text',
      offset: '1 d',
      sent: '467',
      // A text cannot report an open. 23 confirmations is something we actually
      // observe.
      kpiValue: '23',
      kpiLabel: 'confirmed',
    },
    {
      // *Morning of* is gone — it named a time of day, not an offset, and could
      // not be computed from an appointment nobody had looked at yet (§8). The
      // switched-off step is the 2-hour text, which IS an offset.
      key: 's-2h',
      before: true,
      dot: 'var(--ink-4)',
      name: '2 hours before · text',
      purpose: 'Prep · text',
      offset: '2 h',
      sent: '—',
      kpiValue: 'Off',
      kpiLabel: '',
      off: true,
    },
    {
      key: 's-postop',
      dot: 'var(--terracotta)',
      name: 'Post-op checks',
      // The text goes when nobody answers, not when the clock says so.
      purpose: 'Safety · call, text if no answer',
      offset: '12 h',
      sent: '124',
      kpiValue: '81%',
      kpiLabel: 'reached',
    },
    {
      key: 's-noshow',
      dot: 'var(--gold)',
      name: 'No-show follow-up',
      purpose: 'Rebook · text',
      offset: '+1 d',
      sent: '31',
      kpiValue: '39%',
      kpiLabel: 'rebooked',
    },
    {
      key: 's-review',
      dot: 'var(--success)',
      name: 'The review ask',
      purpose: 'Reviews · text',
      offset: '+5 d',
      sent: '142',
      // There is no Google connection in V1, so *new reviews* was a number we
      // could not have. What we know is how many we asked.
      kpiValue: '142 asked',
      kpiLabel: '0.4% opted out',
    },
  ],
  // Three channels, and the confirmations add to the headline's 812. The fourth
  // row was *AI call · post-op safety* — a different measurement (people reached
  // on a check) presented as a channel's confirm rate. Its figures moved to the
  // *Complications caught* tile, which is what they describe.
  channels: [
    { label: 'Email', sent: 430, confirmed: 47 },
    { label: 'Text', sent: 1968, confirmed: 713 },
    { label: 'AI call', sent: 186, confirmed: 52 },
  ],
  activity: [
    { id: 'e1', tone: 'held', text: 'Maria Ortega — confirmation held, outside quiet hours', at: '2 min ago' },
    { id: 'e2', tone: 'outcome', text: 'Owen Brandt confirmed on an AI call — 48 seconds', at: '16 min ago' },
    // The new kind: confirming from the email's button (§6, board RM3Confirm).
    { id: 'e2b', tone: 'outcome', text: 'Devon Achebe confirmed by email', at: '22 min ago' },
    { id: 'e3', tone: 'batch', text: '11 confirmation calls not placed — already confirmed', at: '29 min ago' },
    { id: 'e4', tone: 'outcome', text: 'Priya Shah confirmed Thu 10:00am with Dr. Hwang via SMS', at: '33 min ago' },
    // *the judge* is an internal name for a review step. The practice needs to
    // know ELVA caught it, not what we call the thing that caught it.
    {
      id: 'e5',
      tone: 'agent',
      text: 'ELVA corrected the dentist’s name in Alan Whitfield’s reminder before it went out',
      at: '1h ago',
    },
    { id: 'e6', tone: 'outcome', text: 'Tomas Vela moved to Fri 4:15pm', at: '1h ago' },
    // The balance line is gone: A/R is locked on this page, so a reminder
    // carrying a balance is something this agent cannot do.
    { id: 'e8', tone: 'outcome', text: 'Ruben Diaz — post-op call, nothing to pass on, 41 seconds', at: '3h ago' },
    {
      id: 'e9',
      tone: 'batch',
      text: '6 post-op checks — 4 answered calls, 2 texts after no answer',
      at: '4h ago',
    },
  ],
};
